// ---------------------------------------------------------------------------
// FlowPicker — dropdown to select a flow from available flows.
// Used inside EventBindingRow when adding/changing an event binding.
//
// Features:
//   - Search input to filter flows by name
//   - Grouped by category: init, event, shared (used by 2+ elements)
//   - Action summary for each flow (e.g., "restApi → setState")
// ---------------------------------------------------------------------------

import { useState, useMemo } from 'react'
import { ChevronDown, Plus, Search } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useEditorStore } from '@/features/page-editor/store/editor-store'
import { useStudioStore } from '@/store/studio-store'
import { useCrossReferenceIndex, summarizeFlowActions } from '@/lib/cross-reference'

interface FlowPickerProps {
  value?: string
  availableFlows: string[]
  onSelect: (flowName: string) => void
  /** Return false to reject the creation (e.g. duplicate name) — the picker
   *  then keeps its input state and does NOT bind. */
  onCreateNew?: (flowName: string) => boolean | void
}

interface FlowGroup {
  label: string
  flows: string[]
}

export function FlowPicker({ value, availableFlows, onSelect, onCreateNew }: FlowPickerProps) {
  const [open, setOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [search, setSearch] = useState('')

  const editorFlows = useEditorStore((s) => s.flows)
  // Step summaries prefer the bridge-maintained currentProject.flows mirror —
  // editorStore.flows step arrays are frozen at load time, so summaries would
  // lie after Behavior-surface step edits (F8 secondary staleness).
  const projectFlows = useStudioStore((s) => s.currentProject?.flows)
  const xref = useCrossReferenceIndex()

  // Filter flows by search query
  const filtered = useMemo(() => {
    if (!search.trim()) return availableFlows
    const q = search.toLowerCase()
    return availableFlows.filter((f) => f.toLowerCase().includes(q))
  }, [availableFlows, search])

  // Group flows into categories
  const groups = useMemo((): FlowGroup[] => {
    const initFlows: string[] = []
    const sharedFlows: string[] = []
    const eventFlows: string[] = []

    for (const name of filtered) {
      const lowerName = name.toLowerCase()
      if (lowerName.startsWith('init') || lowerName.startsWith('onload') || lowerName.startsWith('on_load')) {
        initFlows.push(name)
      } else {
        const usages = xref.flowUsageIndex.get(name)
        if (usages && usages.length >= 2) {
          sharedFlows.push(name)
        } else {
          eventFlows.push(name)
        }
      }
    }

    const result: FlowGroup[] = []
    if (initFlows.length > 0) result.push({ label: 'Init', flows: initFlows })
    if (eventFlows.length > 0) result.push({ label: 'Event', flows: eventFlows })
    if (sharedFlows.length > 0) result.push({ label: 'Shared', flows: sharedFlows })
    return result
  }, [filtered, xref])

  const handleSelect = (flow: string) => {
    onSelect(flow)
    setOpen(false)
    setSearch('')
  }

  const handleCreate = () => {
    const trimmed = newName.trim()
    if (!trimmed) return
    // false = creation rejected (duplicate name): the creator surfaced the
    // error — keep the input state so the user can adjust, and do NOT bind.
    if (onCreateNew?.(trimmed) === false) return
    onSelect(trimmed)
    setNewName('')
    setCreating(false)
    setOpen(false)
    setSearch('')
  }

  return (
    <div className="relative">
      <button
        type="button"
        className={cn(
          'flex items-center justify-between gap-1 w-full px-2 py-1 text-xs rounded border border-input',
          'hover:bg-muted/50 transition-colors text-left',
          open && 'border-ring',
        )}
        onClick={() => setOpen(!open)}
      >
        <span className={cn('truncate', !value && 'text-muted-foreground')}>
          {value || 'Select flow...'}
        </span>
        <ChevronDown className={cn('h-3 w-3 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-popover border border-border rounded-xl shadow-lg py-0.5 max-h-56 overflow-y-auto">
          {/* Search input */}
          {availableFlows.length > 3 && (
            <div className="flex items-center gap-1 px-2 py-1 border-b border-border">
              <Search className="h-3 w-3 text-muted-foreground shrink-0" />
              <input
                autoFocus
                className="flex-1 text-xs bg-transparent outline-none"
                placeholder="Search flows..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}

          {filtered.length === 0 && !creating && (
            <div className="px-2 py-1.5 text-[10px] text-muted-foreground">
              {search ? 'No matching flows' : 'No flows available'}
            </div>
          )}

          {/* Grouped flow list */}
          {groups.map((group) => (
            <div key={group.label}>
              {groups.length > 1 && (
                <div className="px-2 pt-1.5 pb-0.5 text-[9px] font-semibold uppercase tracking-widest text-muted-foreground/70">
                  {group.label}
                </div>
              )}
              {group.flows.map((flow) => {
                const cogoFlow = projectFlows?.[flow] ?? editorFlows[flow]
                const summary = cogoFlow ? summarizeFlowActions(cogoFlow) : null
                return (
                  <button
                    key={flow}
                    type="button"
                    className={cn(
                      'w-full px-2 py-1 text-left text-xs hover:bg-accent transition-colors',
                      value === flow && 'bg-accent/50 font-medium',
                    )}
                    onClick={() => handleSelect(flow)}
                  >
                    <div className="truncate">{flow}</div>
                    {summary && summary !== 'empty' && (
                      <div className="text-[9px] font-mono text-muted-foreground/60 truncate">{summary}</div>
                    )}
                  </button>
                )
              })}
            </div>
          ))}

          {creating ? (
            <div className="flex items-center gap-1 px-1.5 py-1 border-t border-border mt-0.5">
              <input
                autoFocus
                className="flex-1 text-xs bg-transparent outline-none px-1 py-0.5 border border-input rounded"
                placeholder="Flow name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCreate()
                  if (e.key === 'Escape') { setCreating(false); setNewName('') }
                }}
              />
              <button
                type="button"
                className="text-[10px] px-1.5 py-0.5 rounded bg-primary text-primary-foreground hover:bg-primary/90"
                onClick={handleCreate}
              >
                Add
              </button>
            </div>
          ) : (
            onCreateNew && (
              <button
                type="button"
                className="w-full px-2 py-1 text-left text-xs hover:bg-accent transition-colors flex items-center gap-1 text-muted-foreground border-t border-border mt-0.5"
                onClick={() => setCreating(true)}
              >
                <Plus className="h-3 w-3" />
                Create new flow
              </button>
            )
          )}
        </div>
      )}
    </div>
  )
}
